import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
    Button, FormInput, Modal, ModalBody,
    ModalHeader, Row, Text, SelectOption, useUI
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { AgreementController } from '@evertel/agreements'
import { APIDataRoom } from '@evertel/types'
import { AgreementTextEditor, AGREEMENT_CONTENT_MAX_LENGTH } from '../elements/AgreementTextEditor'
import { AgreementSettingsFields } from '../elements/AgreementSettingsFields'

interface CreateAgreementModalProps {
    visible: boolean
    onSuccess?: () => void
    onClose: () => void
}

type CreateAgreementForm = {
    name: string
    content: string
    roles: SelectOption[]
    rooms: APIDataRoom[]
    acceptOncePerRoom: boolean
    isActive: boolean
}

const INITIAL_FORM: CreateAgreementForm = {
    name: '',
    content: '',
    roles: [],
    rooms: [],
    acceptOncePerRoom: false,
    isActive: true
}

const CreateAgreementModal: React.FC<CreateAgreementModalProps> = observer(({
    visible,
    onSuccess,
    onClose
}) => {
    const sessionService = useService(SessionService, [])
    const agreementController = useService(AgreementController, [sessionService.selectedDepartmentId])
    const { addToast } = useUI()
    const [step, setStep] = useState(0)
    const [form, setForm] = useState<CreateAgreementForm>(INITIAL_FORM)
    const [contentLength, setContentLength] = useState(0)
    const [isSaving, setIsSaving] = useState(false)
    const [nameError, setNameError] = useState('')

    const isOverLimit = contentLength > AGREEMENT_CONTENT_MAX_LENGTH
    const canContinue = form.name.trim().length > 0 && contentLength > 0 && !isOverLimit

    useEffect(() => {
        if (visible) {
            agreementController.init(sessionService.selectedDepartmentId)
        }
    }, [visible])

    const handleNext = () => {
        setStep(1)
    }

    const handleBack = () => {
        setStep(0)
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setForm(prev => ({ ...prev, name: value }))
        setNameError(value.trim() ? '' : 'Agreement name is required')
    }

    const handleSave = async () => {
        setIsSaving(true)

        try {
            const roomIds = form.rooms.length > 0
                ? form.rooms.filter(r => r.id !== undefined).map(r => r.id as number)
                : []

            await agreementController.createAgreement({
                name: form.name.trim(),
                content: form.content,
                roomIds,
                restrictedRoles: form.roles.map(r => r.value as string),
                acceptOncePerRoom: form.acceptOncePerRoom,
                enabled: form.isActive
            })

            addToast({ color: 'success', title: 'Agreement Created', message: 'Your agreement has been created.' })
            onSuccess?.()
            handleClose()
        } catch (e: any) {
            addToast({ color: 'danger', title: 'Create Unsuccessful', message: e?.message || 'Failed to create agreement' })
        } finally {
            setIsSaving(false)
        }
    }

    const handleClose = () => {
        setForm(INITIAL_FORM)
        setStep(0)
        setNameError('')
        setContentLength(0)
        setIsSaving(false)
        onClose()
    }

    return (
        <Modal
            visible={visible}
            onClose={handleClose}
            size="lg"
            backdrop="static"
        >
            <ModalHeader title="Configure your Agreement below" />
            <ModalBody className="mb-2">
                {step === 0 && (
                    <>
                        <Text bold className="d-block mb-3">Agreement Content</Text>
                        <FormInput
                            id="create-agreement-name"
                            name="name"
                            label="Agreement name"
                            required
                            value={form.name}
                            onChange={handleNameChange}
                            error={nameError}
                            placeholder="Agreement name"
                            className="mb-3"
                            maxLength={90}
                        />
                        <AgreementTextEditor
                            value={form.content}
                            onChange={(val) => setForm(prev => ({ ...prev, content: val }))}
                            minHeight={200}
                            maxLength={AGREEMENT_CONTENT_MAX_LENGTH}
                            onLengthChange={setContentLength}
                        />
                    </>
                )}

                {step === 1 && (
                    <AgreementSettingsFields
                        departmentId={sessionService.selectedDepartmentId}
                        idPrefix="create-agreement"
                        roles={form.roles}
                        onRolesChange={(roles) => setForm(prev => ({ ...prev, roles }))}
                        rooms={form.rooms}
                        onRoomsChange={(rooms) => setForm(prev => ({ ...prev, rooms }))}
                        acceptOncePerRoom={form.acceptOncePerRoom}
                        onAcceptOncePerRoomChange={(checked) => setForm(prev => ({ ...prev, acceptOncePerRoom: checked }))}
                        enabled={form.isActive}
                        onEnabledChange={(checked) => setForm(prev => ({ ...prev, isActive: checked }))}
                    />
                )}
                <Row valign="center" align="between" className="mt-3">
                    <small className="text-muted">Step {step + 1} of 2</small>
                    <div>
                        {step > 0 && (
                            <Button
                                color="link"
                                className="me-2"
                                onClick={handleBack}
                                disabled={isSaving}
                            >
                                Back
                            </Button>
                        )}
                        {step === 0 ? (
                            <Button
                                color="primary"
                                onClick={handleNext}
                                disabled={!canContinue}
                            >
                                Continue
                            </Button>
                        ) : (
                            <Button
                                color="primary"
                                onClick={handleSave}
                                loading={isSaving}
                                disabled={isSaving}
                            >
                                Save
                            </Button>
                        )}
                    </div>
                </Row>
            </ModalBody>
        </Modal>
    )
})

CreateAgreementModal.displayName = 'CreateAgreementModal'
export { CreateAgreementModal }
